import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserProvider, Contract, parseEther } from "ethers";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import MarketplaceItem from "./MarketplaceItem";
import BlockedUserDialog from "./BlockedUserDialog";
import nftContract from "./abis/CarbonCreditNFT.json";
import marketplaceContract from "./abis/CarbonCreditMarketplace.json";
import { useMarketplaceNFTs } from "../hooks/useMarketplaceNFTs";
import { useBlockedUserCheck } from "../hooks/useBlockedUserCheck";
import { toIpfsGatewayUrl } from "../utils/ipfs";

const NFT_ADDRESS = nftContract.networks[process.env.REACT_APP_NETWORK_ID].address;
const MARKETPLACE_ADDRESS = marketplaceContract.networks[process.env.REACT_APP_NETWORK_ID].address;

function formatBidEth(value) {
  if (value == null) return "0.00";
  return (Number(value) / 1e18).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function shortAddress(address) {
  if (!address) return "-";
  const value = String(address);
  if (value.length < 10) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function shortHash(value) {
  if (!value) return "-";
  const text = String(value);
  if (text.length <= 18) return text;
  return `${text.slice(0, 10)}...${text.slice(-8)}`;
}

function normalizeWeiAmount(value) {
  if (typeof value === "bigint") return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0n;
    if (/^\d+$/.test(trimmed)) return BigInt(trimmed);
    return parseEther(trimmed);
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return BigInt(Math.max(0, Math.trunc(value)));
  }

  return 0n;
}

export default function MarketplaceList({ themeMode, onAuctionFinalized }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const {
    checkUserBlocked,
    blockedDialogOpen,
    blockedUserAddress,
    blockedAction,
    closeBlockedDialog,
  } = useBlockedUserCheck();

  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState("tokenId");
  const [sortOrder, setSortOrder] = useState("desc");

  const [listTokenId, setListTokenId] = useState(null);
  const [listPriceInput, setListPriceInput] = useState("");

  const [bidTokenId, setBidTokenId] = useState(null);
  const [bidAmountInput, setBidAmountInput] = useState("");
  const [bidSubmitting, setBidSubmitting] = useState(false);

  const [metadataTokenId, setMetadataTokenId] = useState(null);

  const [bidHistoryTokenId, setBidHistoryTokenId] = useState(null);
  const [bidHistoryLoading, setBidHistoryLoading] = useState(false);
  const [bidHistory, setBidHistory] = useState([]);

  const [auditHistoryTokenId, setAuditHistoryTokenId] = useState(null);
  const [auditHistoryLoading, setAuditHistoryLoading] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);

  const txBackoffRef = useRef({});

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);
  }, [themeMode]);

  const {
    currentAccount,
    now,
    paginatedNfts,
    filteredTokenIds,
    counts,
    loadingStatus,
    errors,
    tokensById,
    hydrateDynamicToken,
    hydrateFullToken,
    optimisticPatchToken,
    processReceiptLogs,
    fetchBidHistoryForToken,
    fetchAuditHistoryForToken,
  } = useMarketplaceNFTs({
    nftAddress: NFT_ADDRESS,
    marketplaceAddress: MARKETPLACE_ADDRESS,
    nftAbi: nftContract.abi,
    marketplaceAbi: marketplaceContract.abi,
    activeTab,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
  });

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredTokenIds.length / itemsPerPage)),
    [filteredTokenIds.length, itemsPerPage]
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const listToken = listTokenId ? tokensById?.[String(listTokenId)] : null;
  const bidToken = bidTokenId ? tokensById?.[String(bidTokenId)] : null;
  const metadataToken = metadataTokenId ? tokensById?.[String(metadataTokenId)] : null;
  const bidHistoryToken = bidHistoryTokenId ? tokensById?.[String(bidHistoryTokenId)] : null;
  const auditHistoryToken = auditHistoryTokenId ? tokensById?.[String(auditHistoryTokenId)] : null;

  const ensureNotBlocked = useCallback(
    async (provider, action) => {
      const signer = await provider.getSigner();
      const userAddress = await signer.getAddress();
      const blocked = checkUserBlocked(userAddress, action);
      if (blocked) {
        enqueueSnackbar("This wallet is blocked from this action.", { variant: "error" });
        return null;
      }
      return { signer, userAddress };
    },
    [checkUserBlocked, enqueueSnackbar]
  );

  const withScopedBackoff = useCallback(
    async (operationKey, operationFn) => {
      const until = txBackoffRef.current[operationKey] || 0;
      const nowMs = Date.now();
      if (until > nowMs) {
        enqueueSnackbar(
          `Wallet is still processing a previous ${operationKey} request. Please try again shortly.`,
          { variant: "warning" }
        );
        return null;
      }

      try {
        return await operationFn();
      } catch (err) {
        const code = err?.error?.code ?? err?.code;
        const message = String(err?.message || "");
        if (code === -32002 || message.includes("-32002")) {
          txBackoffRef.current[operationKey] = Date.now() + 30000;
        }
        throw err;
      }
    },
    [enqueueSnackbar]
  );

  const getSignerContracts = useCallback(
    async (action) => {
      const provider = new BrowserProvider(window.ethereum);
      const userSession = await ensureNotBlocked(provider, action);
      if (!userSession) return null;

      return {
        provider,
        signer: userSession.signer,
        userAddress: userSession.userAddress,
        nftWrite: new Contract(NFT_ADDRESS, nftContract.abi, userSession.signer),
        marketplaceWrite: new Contract(MARKETPLACE_ADDRESS, marketplaceContract.abi, userSession.signer),
      };
    },
    [ensureNotBlocked]
  );

  const handleCancelListing = useCallback(
    async (tokenId) => {
      try {
        await withScopedBackoff("cancel-listing", async () => {
          const session = await getSignerContracts("cancel listings");
          if (!session) return;

          optimisticPatchToken(tokenId, { pendingOperation: "cancelling" });
          const tx = await session.marketplaceWrite.cancelListing(tokenId);
          const receipt = await tx.wait();
          await processReceiptLogs(receipt);

          optimisticPatchToken(tokenId, {
            isListed: false,
            isAuction: false,
            price: null,
            seller: null,
            pendingOperation: null,
            auctionData: { active: false },
          });

          await hydrateDynamicToken(tokenId, "tx-cancelled");
          enqueueSnackbar(`Listing for NFT #${tokenId} has been cancelled.`, { variant: "success" });
        });
      } catch (err) {
        optimisticPatchToken(tokenId, { pendingOperation: null });
        enqueueSnackbar(`Failed to cancel listing: ${err?.message || "Unknown error"}`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, getSignerContracts, hydrateDynamicToken, optimisticPatchToken, processReceiptLogs, withScopedBackoff]
  );

  const handleListForSale = useCallback(
    async (tokenId, priceEth) => {
      try {
        await withScopedBackoff("list-token", async () => {
          const session = await getSignerContracts("list NFTs for sale");
          if (!session) return;

          optimisticPatchToken(tokenId, { pendingOperation: "listing" });

          const approveTx = await session.nftWrite.approve(MARKETPLACE_ADDRESS, tokenId);
          await approveTx.wait();

          const priceWei = parseEther(priceEth);
          const listTx = await session.marketplaceWrite.listToken(tokenId, priceWei);
          const receipt = await listTx.wait();
          await processReceiptLogs(receipt);

          optimisticPatchToken(tokenId, {
            isListed: true,
            isAuction: false,
            price: priceWei,
            seller: session.userAddress,
            pendingOperation: null,
          });

          await hydrateDynamicToken(tokenId, "tx-listed");
          enqueueSnackbar(`NFT #${tokenId} listed for sale.`, { variant: "success" });
        });
      } catch (err) {
        optimisticPatchToken(tokenId, { pendingOperation: null });
        enqueueSnackbar(`Failed to list token: ${err?.reason || err?.message || "Unknown error"}`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, getSignerContracts, hydrateDynamicToken, optimisticPatchToken, processReceiptLogs, withScopedBackoff]
  );

  const handleBidOnAuction = useCallback(
    async (tokenId, bidAmountEth) => {
      try {
        await withScopedBackoff("place-bid", async () => {
          const session = await getSignerContracts("place bids");
          if (!session) return;

          const bidAmountWei = parseEther(bidAmountEth);
          optimisticPatchToken(tokenId, {
            pendingOperation: "bidding",
            auctionData: {
              highestBid: bidAmountWei,
              highestBidder: session.userAddress,
              active: true,
            },
            price: bidAmountWei,
          });

          const tx = await session.marketplaceWrite.placeBid(tokenId, { value: bidAmountWei });
          const receipt = await tx.wait();
          await processReceiptLogs(receipt);

          optimisticPatchToken(tokenId, { pendingOperation: null });
          await hydrateDynamicToken(tokenId, "tx-bid");
          enqueueSnackbar(`Bid placed for NFT #${tokenId}.`, { variant: "success" });
        });
      } catch (err) {
        optimisticPatchToken(tokenId, { pendingOperation: null });
        enqueueSnackbar(`Failed to place bid: ${err?.reason || err?.message || "Unknown error"}`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, getSignerContracts, hydrateDynamicToken, optimisticPatchToken, processReceiptLogs, withScopedBackoff]
  );

  const handleBuyNFT = useCallback(
    async (tokenId, price) => {
      try {
        await withScopedBackoff("buy-token", async () => {
          const session = await getSignerContracts("buy NFTs");
          if (!session) return;

          const priceWei = normalizeWeiAmount(price);
          if (priceWei <= 0n) {
            throw new Error("Invalid listing price");
          }
          const priceEth = (Number(priceWei) / 1e18).toFixed(3);

          optimisticPatchToken(tokenId, { pendingOperation: "buying" });
          enqueueSnackbar(`Buying NFT #${tokenId} for ${priceEth} ETH`, { variant: "info" });

          const tx = await session.marketplaceWrite.buyToken(tokenId, { value: priceWei });
          const receipt = await tx.wait();
          await processReceiptLogs(receipt);

          optimisticPatchToken(tokenId, {
            pendingOperation: null,
            owner: session.userAddress,
            isListed: false,
            isAuction: false,
            price: null,
            seller: null,
            auctionData: { active: false },
          });

          await hydrateDynamicToken(tokenId, "tx-purchase");
          enqueueSnackbar(`You bought NFT #${tokenId}.`, { variant: "success" });
        });
      } catch (err) {
        optimisticPatchToken(tokenId, { pendingOperation: null });
        enqueueSnackbar(`Failed to buy NFT #${tokenId}: ${err?.message || "Unknown error"}`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, getSignerContracts, hydrateDynamicToken, optimisticPatchToken, processReceiptLogs, withScopedBackoff]
  );

  const handleFinalizeAuction = useCallback(
    async (tokenId) => {
      try {
        await withScopedBackoff("finalize-auction", async () => {
          const session = await getSignerContracts("finalize auctions");
          if (!session) return;

          optimisticPatchToken(tokenId, { pendingOperation: "finalizing" });
          const tx = await session.marketplaceWrite.finalizeAuction(tokenId);
          const receipt = await tx.wait();
          await processReceiptLogs(receipt);

          optimisticPatchToken(tokenId, { pendingOperation: null });
          await hydrateDynamicToken(tokenId, "tx-finalize");

          if (onAuctionFinalized) {
            onAuctionFinalized(tokenId);
          }

          enqueueSnackbar(`Auction for NFT #${tokenId} finalized.`, { variant: "success" });
        });
      } catch (err) {
        optimisticPatchToken(tokenId, { pendingOperation: null });
        enqueueSnackbar(`Failed to finalize auction: ${err?.reason || err?.message || "Unknown error"}`, {
          variant: "error",
        });
      }
    },
    [
      enqueueSnackbar,
      getSignerContracts,
      hydrateDynamicToken,
      onAuctionFinalized,
      optimisticPatchToken,
      processReceiptLogs,
      withScopedBackoff,
    ]
  );

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
  }, []);

  const handleItemsPerPageChange = useCallback((event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback(
    (newSortBy) => {
      if (sortBy === newSortBy) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortBy(newSortBy);
        setSortOrder("desc");
      }
      setCurrentPage(1);
    },
    [sortBy, sortOrder]
  );

  const handleRequestList = useCallback((nft) => {
    setListTokenId(nft.tokenId);
    setListPriceInput("");
  }, []);

  const handleConfirmList = useCallback(async () => {
    if (!listToken || !listPriceInput) return;
    await handleListForSale(listToken.tokenId, listPriceInput);
    setListTokenId(null);
    setListPriceInput("");
  }, [handleListForSale, listPriceInput, listToken]);

  const handleRequestBid = useCallback((nft) => {
    setBidTokenId(nft.tokenId);
    setBidAmountInput("");
  }, []);

  const handleConfirmBid = useCallback(async () => {
    if (!bidToken || !bidAmountInput || Number(bidAmountInput) <= 0) return;
    setBidSubmitting(true);
    try {
      await handleBidOnAuction(bidToken.tokenId, bidAmountInput);
      const latest = await fetchBidHistoryForToken(
        bidToken.tokenId,
        bidToken?.auctionData?.auctionStartBlock
      );
      setBidHistory(Array.isArray(latest) ? latest : []);
      setBidTokenId(null);
      setBidAmountInput("");
    } finally {
      setBidSubmitting(false);
    }
  }, [bidAmountInput, bidToken, fetchBidHistoryForToken, handleBidOnAuction]);

  const handleRequestBidHistory = useCallback(
    async (nft) => {
      setBidHistoryTokenId(nft.tokenId);
      setBidHistoryLoading(true);
      try {
        const data = await fetchBidHistoryForToken(nft.tokenId, nft?.auctionData?.auctionStartBlock);
        setBidHistory(Array.isArray(data) ? data : []);
      } catch {
        setBidHistory([]);
      } finally {
        setBidHistoryLoading(false);
      }
    },
    [fetchBidHistoryForToken]
  );

  const handleRequestAuditHistory = useCallback(
    async (nft) => {
      setAuditHistoryTokenId(nft.tokenId);
      setAuditHistoryLoading(true);
      try {
        const data = await fetchAuditHistoryForToken(nft.tokenId);
        setAuditHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        setAuditHistory([
          {
            event: "Error",
            by: "-",
            date: "-",
            timestamp: 0,
            transactionHash: "",
            extraInfo: err?.message || "Failed to load token history",
          },
        ]);
      } finally {
        setAuditHistoryLoading(false);
      }
    },
    [fetchAuditHistoryForToken]
  );

  const handleRequestMetadata = useCallback((nft) => {
    setMetadataTokenId(nft.tokenId);
    hydrateFullToken(nft.tokenId, "metadata-open").catch(() => {
      // Keep dialog open with current data; hydration retry system handles failures.
    });
  }, [hydrateFullToken]);

  const metadataDialogContent = useMemo(() => {
    if (!metadataToken) return null;
    return {
      name: metadataToken.name || `Carbon Credit #${metadataToken.tokenId}`,
      description: metadataToken.description || "Carbon Credit NFT",
      image: metadataToken.image,
      attributes: metadataToken.attributes || [],
      ...(metadataToken.retired
        ? {
            retired: metadataToken.retired,
            retiredBy: metadataToken.retiredBy,
            retiredTimestamp: metadataToken.retiredTimestamp
              ? String(metadataToken.retiredTimestamp)
              : null,
          }
        : {}),
    };
  }, [metadataToken]);

  const visibleStatusText = useMemo(() => {
    if (loadingStatus.bootstrapping) return "Loading marketplace snapshot...";
    if (loadingStatus.visibleHydration) return "Refreshing visible NFTs...";
    if (loadingStatus.backgroundHydration) return "Hydrating remaining NFTs in background...";
    return "";
  }, [
    loadingStatus.backgroundHydration,
    loadingStatus.bootstrapping,
    loadingStatus.visibleHydration,
  ]);

  const showVisibleSkeletons = useMemo(() => {
    return loadingStatus.visibleHydration && paginatedNfts.length === 0;
  }, [loadingStatus.visibleHydration, paginatedNfts.length]);

  const visibleSkeletonCount = useMemo(() => {
    return Math.max(4, Math.min(itemsPerPage, 12));
  }, [itemsPerPage]);

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button variant={activeTab === "all" ? "contained" : "outlined"} onClick={() => handleTabChange("all")}>
            All ({counts.all})
          </Button>
          <Button
            variant={activeTab === "listed" ? "contained" : "outlined"}
            onClick={() => handleTabChange("listed")}
          >
            Listed ({counts.listed})
          </Button>
          <Button
            variant={activeTab === "auction" ? "contained" : "outlined"}
            onClick={() => handleTabChange("auction")}
          >
            Auctions ({counts.auction})
          </Button>
          <Button
            variant={activeTab === "retired" ? "contained" : "outlined"}
            onClick={() => handleTabChange("retired")}
          >
            Retired ({counts.retired})
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Items per page</InputLabel>
            <Select value={itemsPerPage} label="Items per page" onChange={handleItemsPerPageChange}>
              <MenuItem value={6}>6</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
              <MenuItem value={48}>48</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button
              variant={sortBy === "tokenId" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSortChange("tokenId")}
            >
              Token ID {sortBy === "tokenId" && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
            <Button
              variant={sortBy === "price" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSortChange("price")}
              disabled={activeTab === "retired"}
            >
              Price {sortBy === "price" && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
            <Button
              variant={sortBy === "name" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSortChange("name")}
            >
              Name {sortBy === "name" && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
          </Stack>

          <Typography variant="body2" sx={{ ml: "auto", color: "text.secondary" }}>
            Showing {filteredTokenIds.length === 0 ? 0 : startIndex + 1}-
            {Math.min(endIndex, filteredTokenIds.length)} of {filteredTokenIds.length} items
          </Typography>
        </Stack>

        {errors.global && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errors.global}
          </Alert>
        )}
      </Box>

      <Box sx={{ maxWidth: "90vw", margin: "0 auto", padding: 2 }}>
        {visibleStatusText && (
          <Box
            sx={{
              position: "fixed",
              top: 12,
              right: 12,
              zIndex: (muiTheme) => muiTheme.zIndex.snackbar,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                pointerEvents: "auto",
                backgroundColor: "background.paper",
                color: "text.secondary",
                border: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                boxShadow: 2,
                maxWidth: 360,
              }}
            >
              <Typography variant="caption">{visibleStatusText}</Typography>
            </Box>
          </Box>
        )}

        {showVisibleSkeletons ? (
          <Grid container spacing={2}>
            {Array.from({ length: visibleSkeletonCount }).map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`skeleton-${index}`}>
                <Box
                  sx={{
                    border: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Skeleton variant="text" width="45%" height={28} />
                  <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 1.5, mb: 1 }} />
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="55%" />
                  <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 1, mt: 1 }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : filteredTokenIds.length === 0 ? (
          <Typography>No NFTs found for the selected category.</Typography>
        ) : (
          <>
            <Grid container spacing={2}>
              {paginatedNfts.map((nft) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={nft.tokenId}>
                  <MarketplaceItem
                    nft={nft}
                    now={now}
                    currentAccount={currentAccount}
                    onRequestList={handleRequestList}
                    onCancel={handleCancelListing}
                    onBuy={handleBuyNFT}
                    onRequestBid={handleRequestBid}
                    onFinalizeAuction={handleFinalizeAuction}
                    onRequestBidHistory={handleRequestBidHistory}
                    onRequestAuditHistory={handleRequestAuditHistory}
                    onRequestMetadata={handleRequestMetadata}
                  />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog open={Boolean(listToken)} onClose={() => setListTokenId(null)}>
        <DialogTitle>List NFT #{listToken?.tokenId}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Price (ETH)"
            type="number"
            fullWidth
            value={listPriceInput}
            onChange={(event) => setListPriceInput(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListTokenId(null)}>Cancel</Button>
          <Button onClick={handleConfirmList} variant="contained" disabled={!listPriceInput}>
            List
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(bidToken)} onClose={() => setBidTokenId(null)}>
        <DialogTitle>Place Bid #{bidToken?.tokenId}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Bid Amount (ETH)"
            type="number"
            fullWidth
            value={bidAmountInput}
            onChange={(event) => setBidAmountInput(event.target.value)}
          />
          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            Current bid: {bidToken?.auctionData?.highestBid ? formatBidEth(bidToken.auctionData.highestBid) : "No bids yet"} ETH
          </Typography>
          {bidSubmitting && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="caption">Submitting bid...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBidTokenId(null)} disabled={bidSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBid}
            variant="contained"
            disabled={bidSubmitting || !bidAmountInput || Number(bidAmountInput) <= 0}
          >
            Place Bid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(bidHistoryToken)} onClose={() => setBidHistoryTokenId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Bidding History #{bidHistoryToken?.tokenId}</DialogTitle>
        <DialogContent dividers>
          {bidHistoryLoading ? (
            <Typography>Loading bid history...</Typography>
          ) : bidHistory.length === 0 ? (
            <Typography>No bids yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {bidHistory.map((entry, idx) => (
                <Box key={`${entry.txHash || "bid"}-${idx}`} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {entry.time || "--:--:--"} - {entry.type === "start" ? "Auction started" : entry.type === "ended" ? "Auction ended" : "Bid"} - {shortAddress(entry.bidder)} - {entry.type === "ended" && Number(entry.amount || 0) <= 0 ? "No winning bid" : `${formatBidEth(entry.amount)} ETH`}
                  </Typography>
                  {entry.blockNumber ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                      Block #{entry.blockNumber}
                      {entry.txHash ? ` | ${shortHash(entry.txHash)}` : ""}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBidHistoryTokenId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(auditHistoryToken)} onClose={() => setAuditHistoryTokenId(null)} maxWidth="md" fullWidth>
        <DialogTitle>NFT History - Public Audit #{auditHistoryToken?.tokenId}</DialogTitle>
        <DialogContent dividers>
          {auditHistoryLoading ? (
            <Typography>Loading bounded history...</Typography>
          ) : auditHistory.length === 0 ? (
            <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
              No events found for this NFT.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {auditHistory.map((entry, idx) => (
                <Box
                  key={`${entry.transactionHash || "history"}-${idx}`}
                  sx={{
                    borderBottom: idx < auditHistory.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                    pb: 1,
                  }}
                >
                  <Typography variant="body2">
                    <strong>{entry.event}</strong> by {shortAddress(entry.by)}
                    {entry.extraInfo ? ` - ${entry.extraInfo}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {entry.date}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontFamily: "monospace" }}>
                    Block #{entry.blockNumber || "-"}
                    {entry.transactionHash ? ` | Tx ${shortHash(entry.transactionHash)}` : ""}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditHistoryTokenId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(metadataToken)} onClose={() => setMetadataTokenId(null)} maxWidth="md" fullWidth>
        <DialogTitle>NFT Metadata #{metadataToken?.tokenId}</DialogTitle>
        <DialogContent dividers>
          {metadataDialogContent && (
            <>
              <Typography variant="h6" gutterBottom>
                {metadataDialogContent.name}
              </Typography>

              {metadataDialogContent.description && (
                <Typography variant="body2" paragraph>
                  {metadataDialogContent.description}
                </Typography>
              )}

              {metadataDialogContent.image && (
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  <img
                    src={toIpfsGatewayUrl(metadataDialogContent.image)}
                    alt="NFT"
                    loading="lazy"
                    decoding="async"
                    style={{ maxWidth: "100%", maxHeight: "300px", borderRadius: "8px" }}
                  />
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Raw Metadata
                </Typography>
                <pre
                  style={{
                    background: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    padding: "12px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontFamily: 'Monaco, "Courier New", monospace',
                    overflow: "auto",
                    maxHeight: "220px",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(metadataDialogContent, null, 2)}
                </pre>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataTokenId(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <BlockedUserDialog
        open={blockedDialogOpen}
        onClose={closeBlockedDialog}
        userAddress={blockedUserAddress}
        action={blockedAction}
      />
    </>
  );
}
